import React, { type ComponentProps, useCallback, useMemo } from 'react';
import { Tabs } from 'expo-router';
import { Platform } from 'react-native';

import { MainTabBarV2, theme, useResponsive } from '@sg/garnish';

import { Navbar } from '../../components';
import { useHasUnseenChallenges } from '../../hooks';
import { APP_BACKGROUND, useTabOptions } from '../../routing';

// ─────────────────────────────────────────────────────────────────────────────

export default function TabsLayout() {
  const { currentBreakpoint, minWidth } = useResponsive();
  const hasUnseenChallenges = useHasUnseenChallenges();

  const { homeTabOptions, menuTabOptions, giftTabOptions, loyaltyTabOptions, reorderTabOptions } =
    useTabOptions();

  const tabsDisplayingBadge = hasUnseenChallenges ? LOYALTY_TABS_WITH_BADGE : EMPTY; // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────

  const screenOptions = useMemo<TabsScreenOptions>(
    () => ({
      headerShown: !currentBreakpoint.isXS,
      header: currentBreakpoint.isXS ? undefined : renderNavbar,
      sceneContainerStyle: { backgroundColor: APP_BACKGROUND },
      tabBarStyle: {
        backgroundColor: APP_BACKGROUND,
        borderTopWidth: 1,
        borderTopColor: theme.colors.OPACITY.DARK_KALE.DARK,
        paddingTop: theme.spacing[2],
      },
      tabBarActiveTintColor: theme.colors.KALE,
      tabBarInactiveTintColor: theme.colors.OPACITY.BLACK.DARK,
    }),
    [currentBreakpoint.isXS],
  );

  // ─── Tab Bar ────────────────────────────────────────────────────────

  const renderTabBar = useCallback<TabBarRenderer>(
    (props) => {
      if (minWidth.isSM) return null;

      // `props` comes from this app's resolved copy of expo-router's `Tabs`,
      // while `MainTabBarV2` derives its props from garnish's own copy — the two
      // navigation types are structurally identical but nominally distinct.
      return (
        <MainTabBarV2
          {...(props as unknown as ComponentProps<typeof MainTabBarV2>)}
          tabsDisplayingBadge={tabsDisplayingBadge}
        />
      );
    },
    [minWidth.isSM, tabsDisplayingBadge],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Tabs
      screenOptions={screenOptions}
      tabBar={renderTabBar}
      backBehavior={Platform.select({ web: 'history', default: 'firstRoute' })}
    >
      <Tabs.Screen name="(home)" options={homeTabOptions} />
      <Tabs.Screen name="(menu)" options={menuTabOptions} />
      <Tabs.Screen name="rewards" options={loyaltyTabOptions} />
      <Tabs.Screen name="reorder-tab" options={reorderTabOptions} />
      <Tabs.Screen name="gift" options={giftTabOptions} />

      {/* hide some tabs */}

      <Tabs.Screen name="account" options={{ href: null }} />
      <Tabs.Screen name="menu" options={{ href: null }} />
    </Tabs>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function renderNavbar() {
  return <Navbar />;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const LOYALTY_TABS_WITH_BADGE = ['rewards'] as const;
const EMPTY = [] as const;

// ─── Types ──────────────────────────────────────────────────────────────────

type TabsScreenOptions = NonNullable<ComponentProps<typeof Tabs>['screenOptions']>;

type TabBarRenderer = NonNullable<ComponentProps<typeof Tabs>['tabBar']>;
